import React, { useEffect } from 'react';
import useInView from 'react-cool-inview';
import { Breakpoint, ConnectedProps } from 'types/utils';
import { Sections } from 'features/page-navigation/components/section-refs-context';
import { css, cx } from 'linaria';
import { deleted, added, getLast } from 'utils/set-utils';
import { RootReducer } from 'store/root-reducer';
import { getArticle } from 'common/selectors';
import { getCartItem } from 'features/basket';
import { connect } from 'react-redux';
import { StickyPanel, StickyPanelProps } from './sticky-panel';

interface Section {
  id: string;
  title: string;
  badgeNumber?: number;
}

type StickyPanelMappedState = {
  quantity: number;
  productId?: string;
};
type StickyPanelWithNavigationProps = Omit<
  StickyPanelProps,
  'sections' | 'activeSectionHref' | 'display'
> & {
  sections: Section[];
} & ConnectedProps<StickyPanelMappedState>;

export const ObserverComponent: React.FC<{
  id: string;
  onActivated: (id: string) => void;
  onDeactivated: (id: string) => void;
}> = ({ id, onActivated, onDeactivated }) => {
  const context: Map<string, React.MutableRefObject<HTMLElement | null>> = React.useContext(
    Sections.Context,
  );
  const refObject = context.get(id);
  const [observerCallCount, setObserverCallCount] = React.useState(0);

  const { inView } = useInView({
    ref: refObject?.current ? refObject : undefined,
    rootMargin: '0px 0px -67% 0px',
    threshold: 0,
  });

  React.useEffect(() => {
    setObserverCallCount((c) => c + 1);
  }, [inView]);

  React.useEffect(() => {
    if (observerCallCount > 1) {
      if (inView) {
        onActivated(id);
      } else {
        onDeactivated(id);
      }
    }
  }, [observerCallCount, inView, id, onActivated, onDeactivated]);

  return null;
};

const borderStyle = css`
  & > * {
    background: linear-gradient(#e1e2e5, #e1e2e5) 0 100%/100% 1px no-repeat;
  }
`;

export const StickyPanelWithNavigation: React.FC<StickyPanelWithNavigationProps> = ({
  sections,
  className = '',
  quantity,
  productId,
  ...props
}) => {
  const defaultHref = `#${sections[0].id}`;
  const [defaultActiveHref] = React.useState(defaultHref);
  const [activeHref, setActiveHref] = React.useState(defaultHref);
  const inBasket = React.useMemo(() => quantity > 0, [quantity]);
  const [setOfActivatedIds, updateSetOfActivatedIds] = React.useState(new Set<string>());
  React.useEffect(() => {
    const lastItem = getLast(setOfActivatedIds);
    if (lastItem) {
      setActiveHref(`#${lastItem}`);
    }
  }, [setOfActivatedIds, quantity, productId]);

  // when quantity changes between 0 and 1 we change activeHref
  React.useEffect(() => {
    if (inBasket && activeHref === defaultActiveHref) {
      setActiveHref(`#complements`);
    }
    if (!inBasket && activeHref === '#complements') {
      setActiveHref(defaultActiveHref);
    }
  }, [activeHref, defaultActiveHref, inBasket]);

  const stickyPanelSections = React.useMemo(
    () =>
      sections.map((item: Section) => {
        return {
          href: `#${item.id}`,
          title: item.title,
          badgeNumber: item.badgeNumber,
        };
      }),
    [sections],
  );

  useEffect(() => {
    setActiveHref(defaultHref);
  }, [defaultHref, sections]);

  const onActivatedCb = React.useCallback(
    (id: string) => updateSetOfActivatedIds((s) => added(s, id)),
    [updateSetOfActivatedIds],
  );
  const onDeactivatedCb = React.useCallback(
    (id: string) => updateSetOfActivatedIds((s) => deleted(s, id)),
    [updateSetOfActivatedIds],
  );

  const observerComponents = React.useMemo(() => {
    return sections.map(({ id }: Section) => {
      return (
        <ObserverComponent key={id} id={id} onActivated={onActivatedCb} onDeactivated={onDeactivatedCb} />
      );
    });
  }, [sections, onActivatedCb, onDeactivatedCb]);

  return (
    <>
      {observerComponents}
      <StickyPanel
        data-qa="sticky-panel"
        activeSectionHref={activeHref}
        display={getDisplayValue({ breakpoint: props.breakpoint })}
        sections={stickyPanelSections}
        className={cx(className, borderStyle)}
        {...props}
      />
    </>
  );
};

export function getDisplayValue({ breakpoint }: { breakpoint: Breakpoint }): StickyPanelProps['display'] {
  return breakpoint === 'mobile' ? 'none' : 'only-navigation';
}

export const mapStateToProps = (store: RootReducer): StickyPanelMappedState => {
  const productId = getArticle(store) || '';
  const quantity = getCartItem(productId)(store)?.quantity || 0;

  return {
    productId,
    quantity,
  };
};

export const StickyPanelConnected = connect(mapStateToProps)(StickyPanelWithNavigation);
